import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Eye,
  Star,
  Search,
  ShieldCheck,
  ShieldAlert,
  Store,
  User,
  Mail,
  Phone,
  FileText,
  Building2,
  Landmark,
  X,
  CreditCard,
  Building,
  UserCheck,
  ClipboardList,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { VendorListItem, VendorRating } from '@terra/api-client';
import { Badge, type BadgeTone, Button, EmptyState, Input, Spinner } from '@terra/ui';

import { api } from '../lib/api';

import { SellerPickupPanel } from './SellerPickupPanel';

// ── Shared helpers ────────────────────────────────────────────────────────────

const formatPaise = (p: number) =>
  `₹${(p / 100).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

const toSentenceCase = (str: string) => {
  if (!str) return '';
  return str
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase());
};

const VENDOR_STATUS_TONE: Record<string, BadgeTone> = {
  DRAFT: 'neutral',
  PENDING_APPROVAL: 'warning',
  CHANGES_REQUESTED: 'warning',
  APPROVED: 'success',
  REJECTED: 'danger',
  SUSPENDED: 'danger',
  PENDING: 'warning',
  UNDER_REVIEW: 'warning',
};

const SELLER_STATUS_FILTERS = new Set([
  'APPROVED',
  'PENDING_APPROVAL',
  'CHANGES_REQUESTED',
  'SUSPENDED',
  'REJECTED',
]);

function getSellerStatusParam(searchParams: URLSearchParams) {
  const status = searchParams.get('status');
  return status && SELLER_STATUS_FILTERS.has(status) ? status : '';
}

// ── Store Access toggle ───────────────────────────────────────────────────────

function AccessToggle({
  on,
  onClick,
  disabled,
}: {
  on: boolean;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={on}
      title={on ? 'Store is active — click to suspend' : 'Store is suspended — click to activate'}
      className={`group relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors duration-300 disabled:opacity-50 ${on ? 'bg-green-500' : 'bg-surface-subtle border-border/60 border'}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-300 ${on ? 'translate-x-4' : 'translate-x-0.5'}`}
      />
    </button>
  );
}

// ── KYC slide-over drawer ─────────────────────────────────────────────────────

/**
 * The reason a suspension is given. The seller sees it on their dashboard and
 * in the notice they are sent, so it is required; null when cancelled.
 */
function askSuspensionReason(question: string): string | null {
  for (;;) {
    const input = window.prompt(
      `${question}

Reason (the seller is shown this, e.g. "Repeated late shipments"):`,
    );
    if (input === null) return null;
    if (input.trim().length >= 5) return input.trim();
    alert('Enter a reason of at least 5 characters.');
  }
}

function KycDrawer({ vendorId, onClose }: { vendorId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [reviewNote, setReviewNote] = useState('');

  const selectedVendor = useQuery({
    queryKey: ['vendor-detail', vendorId],
    queryFn: () => api.vendors.getById(vendorId),
  });

  const transitionWithNote = useMutation({
    mutationFn: ({ id, status, note }: { id: string; status: string; note?: string }) =>
      api.vendors.transitionStatus(id, note !== undefined ? { status, note } : { status }),
    onSuccess: () => {
      onClose();
      setReviewNote('');
      void qc.invalidateQueries({ queryKey: ['sellers'] });
      void qc.invalidateQueries({ queryKey: ['vendor-detail', vendorId] });
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="bg-ink/40 absolute inset-0 backdrop-blur-sm"
      />

      {/* Panel */}
      <motion.div
        initial={{ x: '100%', opacity: 0.5 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: '100%', opacity: 0.5 }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        className="border-border/60 bg-surface relative flex h-full w-full max-w-lg flex-col overflow-y-auto border-l shadow-2xl"
      >
        {/* Header */}
        <div className="border-border/60 bg-surface/80 sticky top-0 z-10 flex items-center justify-between border-b px-6 py-5 backdrop-blur-md">
          <div>
            <h3 className="text-ink text-lg font-bold tracking-tight">KYC Review</h3>
            <p className="text-ink-subtle text-xs font-medium">
              Verify seller documents &amp; identity
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="bg-surface-subtle text-ink-muted hover:text-ink hover:bg-surface-subtle/80 flex h-8 w-8 items-center justify-center rounded-full transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 p-6">
          {selectedVendor.isLoading ? (
            <div className="flex h-full items-center justify-center">
              <Spinner size="lg" />
            </div>
          ) : !selectedVendor.data ? (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <ShieldAlert className="mb-2 h-10 w-10 text-red-400" />
              <p className="text-sm font-medium text-red-500">Error loading profile.</p>
            </div>
          ) : (
            (() => {
              const vendor = selectedVendor.data;
              const kyc = vendor.user.kycRecord;
              return (
                <div className="space-y-8">
                  {/* Store Details */}
                  <section className="space-y-3">
                    <h4 className="text-ink-muted flex items-center gap-2 text-xs font-semibold uppercase tracking-wider">
                      <Store className="h-4 w-4" /> Store Details
                    </h4>
                    <div className="bg-surface-subtle/30 border-border/60 space-y-3 rounded-2xl border p-4 text-sm">
                      <div className="flex justify-between">
                        <span className="text-ink-muted">Store Name</span>
                        <span className="text-ink font-semibold">{vendor.storeName}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-ink-muted">Owner</span>
                        <span className="text-ink font-semibold">{vendor.user.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-ink-muted">Contact</span>
                        <span className="text-ink font-semibold">{vendor.user.phone}</span>
                      </div>
                      <div className="border-border/40 mt-2 flex items-center justify-between border-t pt-2">
                        <span className="text-ink-muted">Account Status</span>
                        <Badge tone={VENDOR_STATUS_TONE[vendor.status] ?? 'neutral'}>
                          {toSentenceCase(vendor.status)}
                        </Badge>
                      </div>
                    </div>
                  </section>

                  {/* KYC & Identity */}
                  <section className="space-y-3">
                    <h4 className="text-ink-muted flex items-center gap-2 text-xs font-semibold uppercase tracking-wider">
                      <Building2 className="h-4 w-4" /> Identity &amp; Tax Info
                    </h4>
                    {!kyc ? (
                      <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-orange-300 bg-orange-50/50 p-6 text-center">
                        <ShieldAlert className="mb-2 h-8 w-8 text-orange-400" />
                        <p className="text-sm font-medium text-orange-800">No KYC Record</p>
                        <p className="mt-1 text-xs text-orange-600/80">
                          Vendor has not submitted compliance documents yet.
                        </p>
                      </div>
                    ) : (
                      <div className="bg-surface-subtle/30 border-border/60 space-y-3 rounded-2xl border p-4 text-sm">
                        <div className="flex justify-between">
                          <span className="text-ink-muted flex items-center gap-1.5">
                            <User className="h-3.5 w-3.5" /> Legal Name (PAN)
                          </span>
                          <span className="text-ink font-semibold">{kyc.legalName}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-ink-muted flex items-center gap-1.5">
                            <CreditCard className="h-3.5 w-3.5" /> PAN Number
                          </span>
                          <span className="text-ink font-mono font-semibold">
                            {kyc.panMasked ?? '—'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-ink-muted flex items-center gap-1.5">
                            <Building className="h-3.5 w-3.5" /> GSTIN
                          </span>
                          <span className="text-ink font-mono font-semibold">
                            {kyc.gstin || '—'}
                          </span>
                        </div>
                        <div className="border-border/40 my-2 border-t" />
                        <div className="flex justify-between">
                          <span className="text-ink-muted flex items-center gap-1.5">
                            <Landmark className="h-3.5 w-3.5" /> Bank Holder
                          </span>
                          <span className="text-ink font-semibold">{kyc.bankAccountName}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-ink-muted">Account No.</span>
                          <span className="text-ink font-mono font-semibold">
                            {kyc.bankAccountMasked ?? '—'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-ink-muted">IFSC Code</span>
                          <span className="text-ink font-mono font-semibold">{kyc.bankIfsc}</span>
                        </div>
                        <div className="border-border/40 my-2 border-t pt-2">
                          <div className="flex items-center justify-between">
                            <span className="text-ink-muted">KYC Status</span>
                            <Badge
                              tone={
                                kyc.status === 'APPROVED'
                                  ? 'success'
                                  : kyc.status === 'REJECTED'
                                    ? 'danger'
                                    : 'warning'
                              }
                            >
                              {toSentenceCase(kyc.status)}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    )}
                  </section>

                  {/* GSTIN checks */}
                  {kyc?.gstin && (
                    <section className="space-y-3">
                      <h4 className="text-ink-muted flex items-center gap-2 text-xs font-semibold uppercase tracking-wider">
                        <ShieldCheck className="h-4 w-4" /> GSTIN verification
                      </h4>
                      <div className="bg-surface-subtle/30 border-border/60 space-y-2 rounded-2xl border p-4 text-sm">
                        {!kyc.gstVerification ? (
                          <p className="text-ink-muted text-xs">
                            Not checked — submitted before GSTIN checks existed. The approval checks
                            below still verify its check digit, PAN and state.
                          </p>
                        ) : (
                          <>
                            <div className="flex justify-between">
                              <span className="text-ink-muted">
                                Format, check digit, PAN, state
                              </span>
                              <Badge tone="success">Passed</Badge>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-ink-muted">Registered in</span>
                              <span className="text-ink font-semibold">
                                {kyc.gstVerification.stateName ?? kyc.gstVerification.stateCode}
                              </span>
                            </div>
                            {kyc.gstVerification.portal ? (
                              <>
                                <div className="flex justify-between">
                                  <span className="text-ink-muted">GST portal status</span>
                                  <Badge
                                    tone={kyc.gstVerification.portal.active ? 'success' : 'danger'}
                                  >
                                    {kyc.gstVerification.portal.status ?? 'Unknown'}
                                  </Badge>
                                </div>
                                <div className="flex justify-between gap-3">
                                  <span className="text-ink-muted">Legal name on portal</span>
                                  <span className="text-ink text-right font-semibold">
                                    {kyc.gstVerification.portal.legalName ?? '—'}
                                  </span>
                                </div>
                                {kyc.gstVerification.nameMatches === false && (
                                  <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">
                                    The portal name does not match the KYC legal name (
                                    {kyc.legalName}). Check that this is the same business.
                                  </p>
                                )}
                              </>
                            ) : (
                              <p className="text-ink-muted text-xs">
                                {kyc.gstVerification.portalError ??
                                  'Not checked against the GST portal (no provider configured). Verify it on gst.gov.in before approving.'}
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    </section>
                  )}

                  <SellerPickupPanel vendorId={vendorId} pickup={vendor.pickup} />

                  {/* What blocks approval */}
                  <section className="space-y-2">
                    <h4 className="text-ink-muted flex items-center gap-2 text-xs font-semibold uppercase tracking-wider">
                      <ShieldAlert className="h-4 w-4" /> Ready to approve?
                    </h4>
                    {vendor.approvalBlockers.length === 0 ? (
                      <p className="rounded-xl bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-800">
                        Everything needed to go live is in place.
                      </p>
                    ) : (
                      <ul className="space-y-1 rounded-xl bg-red-50 px-3 py-2 text-xs font-semibold text-red-800">
                        {vendor.approvalBlockers.map((b) => (
                          <li key={b}>• {b}</li>
                        ))}
                      </ul>
                    )}
                    {vendor.latestReviewNote && (
                      <p className="text-ink-muted text-xs">
                        Last note to the seller: {vendor.latestReviewNote}
                      </p>
                    )}
                  </section>

                  {/* Documents */}
                  {kyc && (
                    <section className="space-y-3">
                      <h4 className="text-ink-muted flex items-center gap-2 text-xs font-semibold uppercase tracking-wider">
                        <FileText className="h-4 w-4" /> Documents
                      </h4>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <KycDocPreview label="PAN Card" url={kyc.panDocUrl} required />
                        <KycDocPreview label="Address Proof" url={kyc.addressProofUrl} required />
                        <KycDocPreview label="GST Certificate" url={kyc.gstDocUrl} />
                      </div>
                    </section>
                  )}
                </div>
              );
            })()
          )}
        </div>

        {/* Sticky Footer */}
        {selectedVendor.data && (
          <div className="border-border/60 bg-surface sticky bottom-0 border-t p-6 shadow-[0_-4px_20px_-10px_rgba(0,0,0,0.1)]">
            <label className="text-ink-muted mb-2 block text-xs font-semibold uppercase tracking-wider">
              Review Notes
            </label>
            <textarea
              placeholder="Add reason for rejection, feedback, or approval notes..."
              value={reviewNote}
              onChange={(e) => setReviewNote(e.target.value)}
              className="border-border bg-surface text-ink focus:border-gold-500 focus:ring-gold-500/20 mb-4 min-h-[80px] w-full resize-none rounded-xl border p-3 text-sm outline-none transition-all focus:ring-2"
            />
            <div className="grid grid-cols-3 gap-3">
              <Button
                variant="secondary"
                disabled={transitionWithNote.isPending}
                onClick={() =>
                  transitionWithNote.mutate({
                    id: selectedVendor.data.id,
                    status: 'CHANGES_REQUESTED',
                    note: reviewNote.trim() || 'Please provide clearer documents.',
                  })
                }
              >
                Request Fix
              </Button>
              <Button
                variant="outline"
                className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                disabled={transitionWithNote.isPending}
                onClick={() => {
                  if (window.confirm('Are you sure you want to reject this vendor store?')) {
                    transitionWithNote.mutate({
                      id: selectedVendor.data.id,
                      status: 'REJECTED',
                      note: reviewNote.trim() || 'Rejected due to non-compliance.',
                    });
                  }
                }}
              >
                Reject
              </Button>
              <Button
                disabled={
                  transitionWithNote.isPending || selectedVendor.data.approvalBlockers.length > 0
                }
                title={
                  selectedVendor.data.approvalBlockers.length > 0
                    ? 'Resolve the items under "Ready to approve?" first'
                    : undefined
                }
                onClick={() =>
                  transitionWithNote.mutate({
                    id: selectedVendor.data.id,
                    status: 'APPROVED',
                    note: reviewNote.trim() || 'Approved successfully.',
                  })
                }
              >
                Approve KYC
              </Button>
            </div>
            {transitionWithNote.isError && (
              <p role="alert" className="mt-3 text-xs font-semibold text-red-600">
                {(
                  transitionWithNote.error as {
                    response?: { data?: { error?: { message?: string } } };
                  }
                )?.response?.data?.error?.message ?? 'That did not work.'}
              </p>
            )}
          </div>
        )}
      </motion.div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// TAB 1 — SELLER OPS
// ══════════════════════════════════════════════════════════════════

function SellerOpsTab() {
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const statusParam = getSellerStatusParam(searchParams);
  const [searchDraft, setSearchDraft] = useState('');
  const [verifyDraft, setVerifyDraft] = useState('');
  const [approvalDraft, setApprovalDraft] = useState(statusParam);
  const [applied, setApplied] = useState({ search: '', verify: '', approval: statusParam });
  const [showAdd, setShowAdd] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkAction, setBulkAction] = useState('');
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '', storeName: '' });

  useEffect(() => {
    setApprovalDraft(statusParam);
    setApplied((prev) => ({ ...prev, approval: statusParam }));
  }, [statusParam]);

  const vendors = useQuery({
    queryKey: ['sellers', applied.search, applied.approval],
    queryFn: () =>
      api.vendors.list({
        pageSize: 100,
        ...(applied.search ? { q: applied.search } : {}),
        ...(applied.approval ? { status: applied.approval } : {}),
      }),
  });

  const create = useMutation({
    mutationFn: () =>
      api.vendors.create({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        password: form.password,
        storeName: form.storeName.trim(),
      }),
    onSuccess: () => {
      setForm({ name: '', email: '', phone: '', password: '', storeName: '' });
      setShowAdd(false);
      void qc.invalidateQueries({ queryKey: ['sellers'] });
    },
  });

  const transition = useMutation({
    mutationFn: ({ id, status, note }: { id: string; status: string; note?: string }) =>
      api.vendors.transitionStatus(id, note ? { status, note } : { status }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['sellers'] }),
    onError: (e: any) =>
      alert(e?.response?.data?.error?.message ?? e?.message ?? 'Could not change the store'),
  });

  // One-off: register every live store's pickup at Shiprocket (new stores are
  // registered on approval; this covers the ones approved before that).
  const syncPickups = useMutation({
    mutationFn: () => api.vendors.syncAllPickups(),
    onSuccess: (r) => {
      const failed = r.results.filter((x) => x.status === 'FAILED');
      alert(
        `Shiprocket pickups — ${r.synced} ready, ${r.pending} awaiting phone verification, ${r.failed} not registered.` +
          (failed.length
            ? '\n\n' + failed.map((f) => `• ${f.storeName}: ${f.error ?? 'failed'}`).join('\n')
            : ''),
      );
    },
    onError: (e: unknown) => alert(e instanceof Error ? e.message : 'Could not sync pickups'),
  });

  const bulkSuspend = useMutation({
    mutationFn: ({ ids, note }: { ids: string[]; note: string }) =>
      api.vendors.bulkTransitionStatus({ ids, status: 'SUSPENDED', note }),
    onSuccess: (data) => {
      setSelected(new Set());
      setBulkAction('');
      if (data?.failed > 0) alert(`Bulk suspend partial failure: ${data.failed} failed.`);
      void qc.invalidateQueries({ queryKey: ['sellers'] });
    },
  });

  let rows = vendors.data?.data ?? [];
  if (applied.verify === 'VERIFIED') rows = rows.filter((v) => v.user.emailVerified);
  if (applied.verify === 'UNVERIFIED') rows = rows.filter((v) => !v.user.emailVerified);

  const allChecked = rows.length > 0 && selected.size === rows.length;
  function toggleRow(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function applyFilters() {
    setApplied({ search: searchDraft.trim(), verify: verifyDraft, approval: approvalDraft });
    const next = new URLSearchParams(searchParams);
    if (approvalDraft) next.set('status', approvalDraft);
    else next.delete('status');
    setSearchParams(next, { replace: true });
  }

  return (
    <div className="space-y-5">
      {/* Create Seller form */}
      <AnimatePresence>
        {showAdd && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="border-border/60 bg-surface overflow-hidden rounded-2xl border p-6 shadow-sm"
          >
            <h3 className="text-ink-muted mb-4 text-[12px] font-bold uppercase tracking-wider">
              Create New Seller
            </h3>
            <form
              className="grid gap-4 sm:grid-cols-3"
              onSubmit={(e) => {
                e.preventDefault();
                if (
                  form.name &&
                  form.email &&
                  form.phone &&
                  form.password.length >= 8 &&
                  form.storeName
                )
                  create.mutate();
              }}
            >
              <Input
                label="Owner Name"
                placeholder="e.g. Rahul Sharma"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                required
              />
              <Input
                label="Store Name"
                placeholder="e.g. Tech World"
                value={form.storeName}
                onChange={(e) => setForm((f) => ({ ...f, storeName: e.target.value }))}
                required
              />
              <Input
                label="Email Address"
                placeholder="rahul@example.com"
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                required
              />
              <Input
                label="Phone Number"
                placeholder="10-digit number"
                value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                required
              />
              <Input
                label="Password"
                placeholder="Secure password"
                type="password"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                hint="Minimum 8 characters"
                required
              />
              <div className="flex items-end">
                <Button type="submit" loading={create.isPending} className="h-10 w-full">
                  Create Seller
                </Button>
              </div>
              {create.isError && (
                <p className="text-[12px] font-semibold text-red-600 sm:col-span-full">
                  Could not create — please ensure email and phone are unique.
                </p>
              )}
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main card */}
      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        {/* Toolbar */}
        <div className="border-border/60 bg-surface-subtle/30 flex flex-col flex-wrap items-center justify-between gap-3 border-b px-5 py-4 sm:flex-row">
          {/* Left: Bulk action */}
          <div className="flex items-center gap-2">
            <select
              value={bulkAction}
              onChange={(e) => setBulkAction(e.target.value)}
              className="border-border/60 bg-surface text-ink focus:border-ink focus:ring-ink/20 h-9 rounded-xl border px-3 text-[13px] font-medium outline-none transition-colors focus:ring-2"
            >
              <option value="">Bulk Action</option>
              <option value="SUSPEND">Suspend Selected</option>
            </select>
            <button
              type="button"
              disabled={bulkAction !== 'SUSPEND' || selected.size === 0 || bulkSuspend.isPending}
              onClick={() => {
                const note = askSuspensionReason(`Suspend ${selected.size} seller(s)?`);
                if (note) bulkSuspend.mutate({ ids: [...selected], note });
              }}
              className="border-border/60 bg-surface text-ink hover:bg-surface-subtle flex h-9 items-center rounded-xl border px-3 text-[13px] font-bold shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md disabled:opacity-50 disabled:hover:translate-y-0"
            >
              Apply
            </button>
            {selected.size > 0 && (
              <span className="bg-ink text-surface inline-flex h-6 min-w-[24px] items-center justify-center rounded-md px-1.5 text-[11px] font-bold">
                {selected.size}
              </span>
            )}
          </div>

          {/* Right: Filters + Add */}
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
            <button
              type="button"
              disabled={syncPickups.isPending}
              onClick={() => {
                if (
                  window.confirm(
                    'Register every live seller’s pickup address with Shiprocket? Sellers without a complete pickup address are listed so you can follow up.',
                  )
                )
                  syncPickups.mutate();
              }}
              className="border-border/60 bg-surface text-ink hover:bg-surface-subtle flex h-9 items-center rounded-xl border px-3 text-[13px] font-bold shadow-sm disabled:opacity-50"
              title="Register pickups for sellers approved before automatic registration"
            >
              {syncPickups.isPending ? 'Syncing…' : 'Sync pickups to Shiprocket'}
            </button>
            <select
              value={approvalDraft}
              onChange={(e) => setApprovalDraft(e.target.value)}
              className="border-border/60 bg-surface text-ink focus:border-ink focus:ring-ink/20 h-9 w-full rounded-xl border px-3 text-[13px] font-medium outline-none transition-colors focus:ring-2 sm:w-auto"
            >
              <option value="">All Status</option>
              <option value="APPROVED">Approved</option>
              <option value="PENDING_APPROVAL">Pending Approval</option>
              <option value="CHANGES_REQUESTED">Changes Requested</option>
              <option value="SUSPENDED">Suspended</option>
              <option value="REJECTED">Rejected</option>
            </select>
            <select
              value={verifyDraft}
              onChange={(e) => setVerifyDraft(e.target.value)}
              className="border-border/60 bg-surface text-ink focus:border-ink focus:ring-ink/20 h-9 w-full rounded-xl border px-3 text-[13px] font-medium outline-none transition-colors focus:ring-2 sm:w-auto"
            >
              <option value="">All Verification</option>
              <option value="VERIFIED">Email Verified</option>
              <option value="UNVERIFIED">Unverified</option>
            </select>
            <div className="relative w-full sm:w-56">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Search className="text-ink-muted h-4 w-4" strokeWidth={2} />
              </div>
              <input
                type="text"
                placeholder="Search store, name, email…"
                value={searchDraft}
                onChange={(e) => setSearchDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') applyFilters();
                }}
                className="border-border/80 bg-surface text-ink focus:border-ink focus:ring-ink/20 placeholder:text-ink-subtle h-9 w-full rounded-xl border pl-9 pr-3 text-[13px] font-medium shadow-sm outline-none transition-colors focus:ring-2"
              />
            </div>
            <button
              onClick={applyFilters}
              className="bg-ink hover:bg-ink/90 flex h-9 w-full items-center justify-center rounded-xl px-4 text-[13px] font-bold text-white shadow-sm transition-all sm:w-auto"
            >
              Filter
            </button>
            <div className="bg-border/60 mx-1 hidden h-6 w-px sm:block" />
            <button
              onClick={() => setShowAdd((v) => !v)}
              className="bg-ink hover:bg-ink/90 flex h-9 w-full items-center justify-center rounded-xl px-4 text-[13px] font-bold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md sm:w-auto"
            >
              {showAdd ? 'Close Form' : '+ Add New Seller'}
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="p-0">
          {vendors.isLoading ? (
            <div className="grid min-h-[300px] place-items-center">
              <Spinner size="lg" />
            </div>
          ) : rows.length === 0 ? (
            <div className="py-12">
              <EmptyState
                icon={<Store className="text-ink-muted h-10 w-10" />}
                title="No sellers found"
                description="Sellers matching your criteria will appear here."
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-border/60 text-ink-muted border-b text-[10px] font-bold uppercase tracking-wider">
                    <th className="py-3 pl-5 pr-3">
                      <input
                        type="checkbox"
                        checked={allChecked}
                        onChange={() =>
                          setSelected(allChecked ? new Set() : new Set(rows.map((v) => v.id)))
                        }
                        className="accent-gold-500 h-4 w-4 cursor-pointer rounded border-gray-300"
                      />
                    </th>
                    <th className="py-3 pr-4">Store Info</th>
                    <th className="py-3 pr-4">Contact</th>
                    <th className="py-3 pr-4 text-center">Products</th>
                    <th className="py-3 pr-4">Due Balance</th>
                    <th className="py-3 pr-4 text-center">Store Access</th>
                    <th className="py-3 pr-4">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-border/40 divide-y text-[13px]">
                  <AnimatePresence>
                    {rows.map((v: VendorListItem, idx: number) => {
                      const approved = v.status === 'APPROVED';
                      return (
                        <motion.tr
                          key={v.id}
                          initial={{ opacity: 0, y: 5 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: idx * 0.02 }}
                          className="hover:bg-surface-subtle/30 group transition-colors"
                        >
                          <td className="py-3 pl-5 pr-3">
                            <input
                              type="checkbox"
                              checked={selected.has(v.id)}
                              onChange={() => toggleRow(v.id)}
                              className="accent-gold-500 h-4 w-4 cursor-pointer rounded border-gray-300"
                            />
                          </td>
                          <td className="py-3 pr-4">
                            <div className="flex items-center gap-3">
                              <div className="bg-surface-subtle text-ink-muted flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-xs font-bold">
                                {v.storeName.slice(0, 1).toUpperCase()}
                              </div>
                              <div className="min-w-0">
                                <p className="text-ink truncate font-semibold">{v.storeName}</p>
                                <p className="text-ink-subtle truncate text-xs">{v.user.name}</p>
                              </div>
                            </div>
                          </td>
                          <td className="text-ink-muted py-3 pr-4">
                            <div className="space-y-0.5 text-[12px]">
                              <p className="flex items-center gap-1.5">
                                <Phone className="text-ink-subtle h-3 w-3" />
                                {v.user.phone}
                              </p>
                              <p className="flex items-center gap-1.5">
                                <Mail className="text-ink-subtle h-3 w-3" />
                                <span className="max-w-[140px] truncate">{v.user.email}</span>
                              </p>
                            </div>
                          </td>
                          <td className="text-ink-muted py-3 pr-4 text-center">
                            <span className="bg-surface-subtle inline-flex h-6 min-w-[24px] items-center justify-center rounded-md px-1.5 text-[11px] font-bold">
                              {v._count.products}
                            </span>
                          </td>
                          <td className="py-3 pr-4">
                            <span className="text-ink font-bold">{formatPaise(v.duePaise)}</span>
                          </td>
                          <td className="py-3 pr-4 text-center">
                            <AccessToggle
                              on={approved}
                              disabled={transition.isPending}
                              onClick={() => {
                                if (!approved) {
                                  transition.mutate({ id: v.id, status: 'APPROVED' });
                                  return;
                                }
                                const note = askSuspensionReason(`Suspend ${v.storeName}?`);
                                if (note)
                                  transition.mutate({ id: v.id, status: 'SUSPENDED', note });
                              }}
                            />
                          </td>
                          <td className="py-3 pr-4">
                            <div className="flex flex-col items-start gap-1">
                              <Badge tone={VENDOR_STATUS_TONE[v.status] ?? 'neutral'}>
                                {toSentenceCase(v.status)}
                              </Badge>
                              {v.user.emailVerified ? (
                                <span className="flex items-center gap-1 text-[10px] font-semibold text-green-600">
                                  <ShieldCheck className="h-3 w-3" /> Verified
                                </span>
                              ) : (
                                <span className="flex items-center gap-1 text-[10px] font-semibold text-orange-500">
                                  <ShieldAlert className="h-3 w-3" /> Unverified
                                </span>
                              )}
                            </div>
                          </td>
                        </motion.tr>
                      );
                    })}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// TAB 2 — COMPLIANCE (KYC)
// ══════════════════════════════════════════════════════════════════

const KYC_QUEUE_STATUSES = ['PENDING_APPROVAL', 'CHANGES_REQUESTED', 'PENDING', 'UNDER_REVIEW'];

function ComplianceTab() {
  const [searchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState('QUEUE');
  const [searchDraft, setSearchDraft] = useState('');
  const [applied, setApplied] = useState({ search: '', status: 'QUEUE' });
  // A notification about a seller application links straight to its review.
  const [kycVendorId, setKycVendorId] = useState<string | null>(searchParams.get('review'));

  const vendors = useQuery({
    queryKey: ['sellers-kyc', applied.search, applied.status],
    queryFn: () =>
      api.vendors.list({
        // 100 is the ceiling `PaginationQuery` allows. Asking for more does not
        // return more — the request fails validation outright, and the empty
        // result renders as "Queue is clear!", which reads as *no submissions*
        // rather than *the request never succeeded*. A seller waiting on
        // approval is invisible to whoever has to approve them.
        pageSize: 100,
        ...(applied.search ? { q: applied.search } : {}),
        // When "QUEUE" is selected we can't pass a multi-status param — filter client-side
      }),
  });

  let rows = vendors.data?.data ?? [];

  // Client-side status filter
  if (applied.status === 'QUEUE') {
    rows = rows.filter((v) => KYC_QUEUE_STATUSES.includes(v.status));
  } else if (applied.status && applied.status !== 'ALL') {
    rows = rows.filter((v) => v.status === applied.status);
  }

  if (applied.search) {
    const q = applied.search.toLowerCase();
    rows = rows.filter(
      (v) =>
        v.storeName.toLowerCase().includes(q) ||
        v.user.name.toLowerCase().includes(q) ||
        v.user.email.toLowerCase().includes(q),
    );
  }

  const pendingCount = (vendors.data?.data ?? []).filter((v) =>
    KYC_QUEUE_STATUSES.includes(v.status),
  ).length;

  return (
    <div className="space-y-5">
      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        {/* Toolbar */}
        <div className="border-border/60 bg-surface-subtle/30 flex flex-col flex-wrap items-center justify-between gap-3 border-b px-5 py-4 sm:flex-row">
          <div className="flex items-center gap-3">
            <div className="bg-surface-subtle text-ink-muted ring-border/60 grid h-8 w-8 place-items-center rounded-lg ring-1 ring-inset">
              <ClipboardList className="h-4 w-4" strokeWidth={2} />
            </div>
            <div>
              <p className="text-ink text-[14px] font-bold">KYC Review Queue</p>
              {pendingCount > 0 && (
                <p className="text-ink-muted text-[11px] font-medium">
                  <span className="font-bold text-orange-500">{pendingCount}</span> awaiting review
                </p>
              )}
            </div>
          </div>

          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border-border/60 bg-surface text-ink focus:border-ink focus:ring-ink/20 h-9 rounded-xl border px-3 text-[13px] font-medium outline-none transition-colors focus:ring-2"
            >
              <option value="QUEUE">Action Required (Pending + Changes)</option>
              <option value="ALL">All Sellers</option>
              <option value="APPROVED">Approved</option>
              <option value="PENDING_APPROVAL">Pending Approval</option>
              <option value="CHANGES_REQUESTED">Changes Requested</option>
              <option value="REJECTED">Rejected</option>
            </select>
            <div className="relative w-full sm:w-56">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Search className="text-ink-muted h-4 w-4" strokeWidth={2} />
              </div>
              <input
                type="text"
                placeholder="Search store or owner…"
                value={searchDraft}
                onChange={(e) => setSearchDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter')
                    setApplied({ search: searchDraft.trim(), status: statusFilter });
                }}
                className="border-border/80 bg-surface text-ink focus:border-ink focus:ring-ink/20 placeholder:text-ink-subtle h-9 w-full rounded-xl border pl-9 pr-3 text-[13px] font-medium shadow-sm outline-none transition-colors focus:ring-2"
              />
            </div>
            <button
              onClick={() => setApplied({ search: searchDraft.trim(), status: statusFilter })}
              className="bg-ink hover:bg-ink/90 flex h-9 w-full items-center justify-center rounded-xl px-4 text-[13px] font-bold text-white shadow-sm transition-all sm:w-auto"
            >
              Filter
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="p-0">
          {vendors.isLoading ? (
            <div className="grid min-h-[300px] place-items-center">
              <Spinner size="lg" />
            </div>
          ) : vendors.isError ? (
            // A failed fetch must not borrow the empty state. "Queue is clear!"
            // on top of an error tells the reviewer there is nothing to do,
            // which is the one wrong answer — sellers sit unapproved and nobody
            // goes looking for a cause.
            <div className="py-16">
              <EmptyState
                icon={<ShieldCheck className="h-10 w-10 text-rose-500" />}
                title="Could not load the review queue"
                description={
                  vendors.error instanceof Error
                    ? vendors.error.message
                    : 'The request failed. This is not an empty queue — retry, and check the API logs if it persists.'
                }
              />
            </div>
          ) : rows.length === 0 ? (
            <div className="py-16">
              <EmptyState
                icon={<ShieldCheck className="text-ink-muted h-10 w-10" />}
                title={applied.status === 'QUEUE' ? 'Queue is clear!' : 'No sellers found'}
                description={
                  applied.status === 'QUEUE'
                    ? 'No pending KYC submissions right now.'
                    : 'Sellers matching your criteria will appear here.'
                }
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-border/60 text-ink-muted border-b text-[10px] font-bold uppercase tracking-wider">
                    <th className="w-16 py-3 pl-5 pr-4">#</th>
                    <th className="py-3 pr-4">Store &amp; Owner</th>
                    <th className="py-3 pr-4">Contact</th>
                    <th className="py-3 pr-4 text-center">KYC Status</th>
                    <th className="py-3 pr-4 text-center">Account Status</th>
                    <th className="py-3 pl-4 pr-5 text-right">Review</th>
                  </tr>
                </thead>
                <tbody className="divide-border/40 divide-y text-[13px]">
                  <AnimatePresence>
                    {rows.map((v: VendorListItem, idx: number) => (
                      <motion.tr
                        key={v.id}
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.02 }}
                        className="hover:bg-surface-subtle/30 group transition-colors"
                      >
                        <td className="text-ink-muted py-3 pl-5 pr-4 font-mono text-[11px] font-bold">
                          {(idx + 1).toString().padStart(2, '0')}
                        </td>
                        <td className="py-3 pr-4">
                          <div className="flex items-center gap-3">
                            <div className="bg-surface-subtle text-ink-muted flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-xs font-bold">
                              {v.storeName.slice(0, 1).toUpperCase()}
                            </div>
                            <div className="min-w-0">
                              <p className="text-ink truncate font-semibold">{v.storeName}</p>
                              <p className="text-ink-subtle truncate text-xs">{v.user.name}</p>
                            </div>
                          </div>
                        </td>
                        <td className="text-ink-muted py-3 pr-4">
                          <div className="space-y-0.5 text-[12px]">
                            <p className="flex items-center gap-1.5">
                              <Phone className="text-ink-subtle h-3 w-3" />
                              {v.user.phone}
                            </p>
                            <p className="flex items-center gap-1.5">
                              <Mail className="text-ink-subtle h-3 w-3" />
                              <span className="max-w-[140px] truncate">{v.user.email}</span>
                            </p>
                          </div>
                        </td>
                        <td className="py-3 pr-4 text-center">
                          {v.user.emailVerified ? (
                            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-green-600">
                              <ShieldCheck className="h-3.5 w-3.5" /> Verified
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-orange-500">
                              <ShieldAlert className="h-3.5 w-3.5" /> Unverified
                            </span>
                          )}
                        </td>
                        <td className="py-3 pr-4 text-center">
                          <Badge tone={VENDOR_STATUS_TONE[v.status] ?? 'neutral'}>
                            {toSentenceCase(v.status)}
                          </Badge>
                        </td>
                        <td className="py-3 pl-4 pr-5 text-right">
                          <button
                            type="button"
                            onClick={() => setKycVendorId(v.id)}
                            className="text-ink-muted hover:text-ink hover:bg-surface-subtle ml-auto flex h-8 w-8 items-center justify-center rounded-lg transition-colors"
                            title="View KYC documents & review"
                          >
                            <Eye className="h-[18px] w-[18px]" strokeWidth={2} />
                          </button>
                        </td>
                      </motion.tr>
                    ))}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* KYC drawer */}
      <AnimatePresence>
        {kycVendorId && <KycDrawer vendorId={kycVendorId} onClose={() => setKycVendorId(null)} />}
      </AnimatePresence>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// ALL SELLERS — tabbed shell
// ══════════════════════════════════════════════════════════════════

type SellerTab = 'ops' | 'compliance';

const TABS: { id: SellerTab; label: string; icon: React.ReactNode; description: string }[] = [
  {
    id: 'ops',
    label: 'Seller Ops',
    icon: <UserCheck className="h-4 w-4" />,
    description: 'Create accounts, toggle store access, bulk suspend',
  },
  {
    id: 'compliance',
    label: 'Compliance (KYC)',
    icon: <ClipboardList className="h-4 w-4" />,
    description: 'Review identity documents, approve or reject KYC submissions',
  },
];

/**
 * One KYC document, shown inline so the admin can read it before approving:
 * an image as a preview, a PDF as a button that opens it. The links are signed
 * and short-lived (the documents are private), so they are fetched fresh with
 * the seller's profile.
 */
function KycDocPreview({
  label,
  url,
  required = false,
}: {
  label: string;
  url: string | null | undefined;
  required?: boolean;
}) {
  if (!url) {
    return (
      <div className="border-border/60 flex items-center gap-2 rounded-xl border border-dashed p-3 text-sm">
        <FileText className="text-ink-muted h-4 w-4" />
        <span className="text-ink font-medium">{label}</span>
        <span className="text-ink-muted ml-auto text-xs">
          {required ? 'Not uploaded or unreadable' : 'Not provided'}
        </span>
      </div>
    );
  }
  const isPdf = /[?&]format=pdf(&|$)|\.pdf(\?|$)/i.test(url);
  return (
    <div className="border-border/60 overflow-hidden rounded-xl border">
      <div className="bg-surface-subtle/40 flex items-center justify-between px-3 py-2">
        <span className="text-ink flex items-center gap-1.5 text-sm font-medium">
          <FileText className="text-gold-500 h-4 w-4" /> {label}
        </span>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-accent text-xs font-semibold hover:underline"
        >
          {isPdf ? 'Open PDF ↗' : 'Full size ↗'}
        </a>
      </div>
      {isPdf ? (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-ink-muted grid h-40 place-items-center bg-white text-xs"
        >
          PDF document — click to open
        </a>
      ) : (
        <a href={url} target="_blank" rel="noopener noreferrer" className="block bg-white">
          <img src={url} alt={label} className="h-40 w-full object-contain" loading="lazy" />
        </a>
      )}
    </div>
  );
}

export function AllSellersPage() {
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState<SellerTab>(
    tabParam === 'compliance' ? 'compliance' : 'ops',
  );

  return (
    <div className="space-y-6 pb-8">
      {/* Tab bar */}
      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        <div className="flex">
          {TABS.map((tab) => {
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`group relative flex flex-1 flex-col items-start gap-0.5 border-b-2 px-6 py-4 text-left transition-all ${
                  active ? 'border-ink bg-surface' : 'hover:bg-surface-subtle/50 border-transparent'
                }`}
              >
                <span
                  className={`flex items-center gap-2 text-[13px] font-bold transition-colors ${
                    active ? 'text-ink' : 'text-ink-muted group-hover:text-ink'
                  }`}
                >
                  {tab.icon}
                  {tab.label}
                </span>
                <span className="text-ink-subtle text-[11px] font-medium leading-snug">
                  {tab.description}
                </span>
                {active && (
                  <motion.span
                    layoutId="seller-tab-indicator"
                    className="bg-ink absolute bottom-0 left-0 right-0 h-[2px] rounded-t"
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.18 }}
        >
          {activeTab === 'ops' ? <SellerOpsTab /> : <ComplianceTab />}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// SELLER RATING & FOLLOWERS (unchanged)
// ══════════════════════════════════════════════════════════════════

export function SellerRatingsPage() {
  const ratings = useQuery({ queryKey: ['seller-ratings'], queryFn: () => api.vendors.ratings() });
  const rows = ratings.data ?? [];

  return (
    <div className="space-y-6 pb-8">
      <div className="bg-surface border-border/60 overflow-hidden rounded-2xl border shadow-sm">
        {ratings.isLoading ? (
          <div className="grid min-h-[300px] place-items-center">
            <Spinner size="lg" />
          </div>
        ) : rows.length === 0 ? (
          <div className="py-12">
            <EmptyState
              icon={<Star className="text-ink-muted h-10 w-10" />}
              title="No data available"
              description="Seller ratings will appear here once customers start reviewing."
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-border/60 bg-surface-subtle/30 text-ink-muted border-b text-[10px] font-bold uppercase tracking-wider">
                  <th className="w-16 py-4 pl-6 pr-4">Rank</th>
                  <th className="py-4 pr-4">Store Profile</th>
                  <th className="py-4 pr-4 text-center">Active Products</th>
                  <th className="py-4 pr-4">Average Rating</th>
                  <th className="py-4 pr-4 text-center">Total Reviews</th>
                  <th className="py-4 pr-6 text-right">Status</th>
                </tr>
              </thead>
              <tbody className="divide-border/40 divide-y text-[13px]">
                <AnimatePresence>
                  {rows.map((v: VendorRating, idx: number) => (
                    <motion.tr
                      key={v.id}
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      className="hover:bg-surface-subtle/30 transition-colors"
                    >
                      <td className="py-4 pl-6 pr-4">
                        <span className="text-ink-muted font-mono font-medium">#{idx + 1}</span>
                      </td>
                      <td className="py-4 pr-4">
                        <div className="flex items-center gap-3">
                          <div className="bg-surface-subtle text-ink-muted flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-xs font-bold">
                            {v.storeName.slice(0, 1).toUpperCase()}
                          </div>
                          <span className="text-ink font-semibold">{v.storeName}</span>
                        </div>
                      </td>
                      <td className="py-4 pr-4 text-center">
                        <span className="bg-surface-subtle inline-flex h-6 min-w-[24px] items-center justify-center rounded-md px-1.5 text-[11px] font-bold">
                          {v.productCount}
                        </span>
                      </td>
                      <td className="py-4 pr-4">
                        <div className="bg-gold-50 border-gold-200/50 inline-flex items-center gap-1.5 rounded-full border px-3 py-1 shadow-sm">
                          <Star className="text-gold-500 h-3.5 w-3.5 fill-current" />
                          <span className="text-gold-800 text-xs font-bold">
                            {(v.avgRatingX100 / 100).toFixed(1)}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 pr-4 text-center">
                        <span className="text-ink-subtle font-medium">{v.reviewCount}</span>
                      </td>
                      <td className="py-4 pr-6 text-right">
                        <Badge tone={VENDOR_STATUS_TONE[v.status] ?? 'neutral'}>
                          {toSentenceCase(v.status)}
                        </Badge>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
